import {
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  Box,
  Grid,
  PopoverAnchor,
} from '@chakra-ui/react';
import { EventContentArg } from '@fullcalendar/common';
import { isSameDay } from 'date-fns';
import { formatInTimeZone, toDate, utcToZonedTime } from 'date-fns-tz';
import { useResourceRelationship } from 'datx-jsonapi-react';
import { useRouter } from 'next/router';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  checkIsLive,
  handleEventClick,
} from '@/components/ui/features/schedule/ScheduleCalendar/ScheduleCalendar.utils';
import { AddToCalendarSession } from '@/components/ui/shared/buttons/AddToCalendar/AddToCalendarSession';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { queryThinEventConfigurationRelationship } from '@/queries/thin-events';
import { EmbeddingScheduleType, EventConfiguration } from '@/resources/EventConfiguration';
import { ThinEventSession } from '@/resources/ThinEventSession';

import { ScheduleCalendarTooltip } from './ScheduleCalendarTooltip';

export interface IScheduleCalendarTrackProps {
  view: string;
  timeZone: string;
  activeDay: string;
  trackData: EventContentArg;
  mutateSession?: () => void;
}

export const ScheduleCalendarTrackEmbedded: FC<IScheduleCalendarTrackProps> = ({
  timeZone,
  trackData,
  view,
  activeDay,
  mutateSession,
}) => {
  const { eventId } = useQueryParams<{ eventId: string }>({
    eventId: StringParam,
  });

  const { event } = trackData;
  const { title, extendedProps, textColor } = event;
  const { session, venue } = extendedProps;
  const { startsAt, endsAt } = session as ThinEventSession;
  const isDayView = view === 'day';
  const isWeekView = view === 'week';

  const { t } = useTranslation();
  const router = useRouter();

  const { data: eventConfiguration } = useResourceRelationship<EventConfiguration>(
    queryThinEventConfigurationRelationship(eventId)
  );

  const timeStart = useMemo(
    () =>
      isSameDay(utcToZonedTime(startsAt, timeZone), toDate(activeDay)) && isDayView
        ? formatInTimeZone(startsAt, timeZone, 'HH:mm')
        : formatInTimeZone(startsAt, timeZone, 'LL/dd/yyyy HH:mm'),
    [startsAt, timeZone, activeDay, isDayView]
  );
  const timeEnd = useMemo(
    () =>
      isSameDay(utcToZonedTime(endsAt, timeZone), toDate(activeDay)) && isDayView
        ? formatInTimeZone(endsAt, timeZone, 'HH:mm')
        : formatInTimeZone(endsAt, timeZone, 'LL/dd/yyyy HH:mm'),
    [endsAt, timeZone, activeDay, isDayView]
  );

  const isSimplified = eventConfiguration?.embeddingScheduleType === EmbeddingScheduleType.SIMPLIFIED;

  const isLive = useMemo(() => checkIsLive(startsAt, endsAt, timeZone), [startsAt, endsAt, timeZone]);
  const handleCalendarTrackClick = useCallback(() => handleEventClick(event, router, !isSimplified), [event, router]); // we use onClick handler because limitation for Calendar lib - <a> tag inside <a> tag

  const room = venue ?? '';

  if (!event?.title || !event?.extendedProps?.session) {
    return null;
  }

  return (
    <Grid
      templateRows="0 1fr"
      h="100%"
      w="100%"
      onClick={handleCalendarTrackClick}
      cursor={!isSimplified && 'pointer'}
      pos="relative"
    >
      <Popover
        arrowPadding={15}
        strategy="fixed"
        placement="top"
        openDelay={500}
        isLazy
        flip
        preventOverflow
        lazyBehavior="unmount"
        trigger="hover"
      >
        <PopoverAnchor>
          <Box position="sticky" top={0} justifySelf="center" />
        </PopoverAnchor>
        <PopoverTrigger>
          <Box w="100%" overflow="hidden" pr={isDayView ? 9 : 0}>
            <Grid templateColumns={isLive ? '60px 1fr' : '1fr'} pl={2}>
              {isLive && !isWeekView && (
                <Box
                  color="#ef5085"
                  fontSize="14px"
                  lineHeight="18px"
                  _before={{
                    content: '""',
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    backgroundColor: 'currentColor',
                    borderRadius: '50%',
                    marginRight: '1ch',
                  }}
                >
                  {t('live')}
                </Box>
              )}
              <Text color={textColor} noOfLines={1} pr={2} fontWeight="bold" fontSize="md" fontFamily="heading">
                {title}
              </Text>
            </Grid>
            <Text
              color={textColor}
              noOfLines={1}
              pr={2}
              pl={2}
              fontSize="sm"
              fontFamily="body"
            >{`${timeStart} - ${timeEnd}`}</Text>
            <Text color={textColor} noOfLines={1} pl={2} fontSize="sm" fontFamily="body">
              {room}
            </Text>
          </Box>
        </PopoverTrigger>
        <Portal>
          <PopoverContent w="100%">
            <PopoverArrow />
            <ScheduleCalendarTooltip
              room={room}
              title={title}
              isLive={isLive}
              session={session}
              timeStart={timeStart}
              timeEnd={timeEnd}
            />
          </PopoverContent>
        </Portal>
      </Popover>
      <Box>
        {isDayView && (
          <AddToCalendarSession
            mutateSession={mutateSession}
            session={extendedProps.session}
            color={textColor}
            timeZone={timeZone}
          />
        )}
      </Box>
    </Grid>
  );
};
